import React, { useState } from 'react';
import { PhotoIcon, DocumentIcon, XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { getFileUrl } from '../../utils/api';

/**
 * FileAttachments component for rendering file attachments with view/download capabilities
 */
const FileAttachments = ({ files = [], onDeleteFile, isReadOnly = false, className = '' }) => {
  const [expandedImage, setExpandedImage] = useState(null);

  // Function to determine if a file is an image
  const isImageFile = (file) => {
    return file.mime_type && file.mime_type.startsWith('image/');
  };

  // Function to handle file download
  const handleDownload = (file, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Build URL with download parameter
    const downloadUrl = `${getFileUrl(file.uuid)}?download=true`;
    
    // Use a fetch request for the download to maintain the session
    fetch(downloadUrl, {
      method: 'GET',
      credentials: 'include', // Important for sending cookies with the request
    })
      .then(response => {
        // Get the filename from the Content-Disposition header if available
        const contentDisposition = response.headers.get('content-disposition');
        let filename = file.original_filename || file.filename;
        
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            filename = fileNameMatch[1].replace(/['"]/g, '');
          }
        }
        
        return response.blob().then(blob => {
          // Create a download link and click it
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(url);
        });
      })
      .catch(error => {
        console.error('Download failed:', error);
        alert('Failed to download file. Please try again.');
      });
  };

  // Function to handle image click for previewing
  const handleImageClick = (file, e) => {
    e.preventDefault();
    if (isImageFile(file)) {
      setExpandedImage(file);
    } else {
      // For non-image files, open in a new tab
      window.open(getFileUrl(file.uuid), '_blank');
    }
  };

  // If no files, don't render anything
  if (!files || files.length === 0) {
    return null;
  }

  return (
    <div className={`mt-3 ${className}`}>
      <ul className="divide-y divide-gray-100 dark:divide-gray-800 rounded-md border border-gray-200 dark:border-gray-700">
        {files.map((file, index) => (
          <li key={file.uuid || index} className="flex items-center justify-between py-2 pl-3 pr-4 text-sm">
            <div className="flex w-0 flex-1 items-center">
              {isImageFile(file) ? (
                <PhotoIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              ) : (
                <DocumentIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
              )}
              
              {file.uuid ? (
                <button 
                  onClick={(e) => handleImageClick(file, e)}
                  className="ml-2 flex-1 w-0 truncate font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 hover:underline text-left cursor-pointer"
                  title={isImageFile(file) ? "View image" : "View file in new tab"}
                >
                  {file.original_filename || file.filename}
                </button>
              ) : (
                <span className="ml-2 flex-1 w-0 truncate">
                  {file.original_filename || file.filename}
                </span>
              )}
            </div>
            <div className="ml-4 flex-shrink-0 flex space-x-2">
              {/* Download button */}
              {file.uuid && (
                <button
                  onClick={(e) => handleDownload(file, e)}
                  className="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
                  title="Download file"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
              
              {/* Delete button - only show if not read-only and onDeleteFile provided */}
              {!isReadOnly && onDeleteFile && (
                <button
                  type="button"
                  onClick={() => onDeleteFile(file)}
                  className="font-medium text-red-600 hover:text-red-500 dark:text-red-400 dark:hover:text-red-300"
                  title="Remove file"
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Image preview modal */}
      {expandedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/80 p-4"
          onClick={() => setExpandedImage(null)}
        >
          <div 
            className="relative max-w-full max-h-full overflow-auto bg-white dark:bg-gray-800 rounded-lg shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2 absolute top-0 right-0">
              <button
                onClick={() => setExpandedImage(null)}
                className="rounded-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 p-1"
              >
                <XMarkIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            <div className="p-4">
              <img 
                src={getFileUrl(expandedImage.uuid)} 
                alt={expandedImage.original_filename || expandedImage.filename}
                className="max-w-full max-h-[80vh] object-contain"
              />
              <div className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                {expandedImage.original_filename || expandedImage.filename}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileAttachments;